// Utility to format a date as a local date string (YYYY-MM-DD)
export const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Utility to create a Date object in local time from a YYYY-MM-DD string.
// Uses explicit (y, m-1, d) so getDay()/getDate() are correct in all timezones;
// "YYYY-MM-DDTHH:mm:ss" can be parsed as UTC and cause off-by-one weekday in western TZ.
export const createLocalDate = (dateString) => {
  const [y, m, d] = dateString.split("-").map(Number);
  return new Date(y, m - 1, d);
};

// Helper function to generate calendar dates for a range
export const generateCalendarDates = (selectedDate, daysBefore = 2, daysAfter = 2) => {
  const dates = [];
  const currentDate = createLocalDate(selectedDate); // Use the utility function

  // Add days before the selected date
  for (let i = daysBefore; i > 0; i--) {
    const date = new Date(currentDate);
    date.setDate(currentDate.getDate() - i);
    dates.push({
      dateString: getLocalDateString(date),
      dayOfWeek: date.toLocaleString('default', { weekday: 'short' }),
      dayNumber: date.getDate(),
    });
  }

  // Add the selected date
  dates.push({
    dateString: getLocalDateString(currentDate),
    dayOfWeek: currentDate.toLocaleString('default', { weekday: 'short' }),
    dayNumber: currentDate.getDate(),
  });

  // Add days after the selected date
  for (let i = 1; i <= daysAfter; i++) {
    const date = new Date(currentDate);
    date.setDate(currentDate.getDate() + i);
    dates.push({
      dateString: getLocalDateString(date),
      dayOfWeek: date.toLocaleString('default', { weekday: 'short' }),
      dayNumber: date.getDate(),
    });
  }

  return dates;
};

// Helper function to get the start and end dates of the week for a given date
export const getWeekRange = (referenceDateString) => {
  // If a date string is passed (YYYY-MM-DD), use that, otherwise today
  const refDate = referenceDateString
    ? createLocalDate(referenceDateString)
    : new Date();

  const startOfWeek = new Date(refDate);
  startOfWeek.setHours(0, 0, 0, 0);
  // Sunday as start of week
  startOfWeek.setDate(refDate.getDate() - refDate.getDay());

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  const toKey = (d) => d.toLocaleDateString("en-CA"); // YYYY-MM-DD
  const toLabel = (d) =>
    `${d.getMonth() + 1}/${d.getDate()}`; // e.g. 8/12

  return {
    startDate: toKey(startOfWeek),
    endDate: toKey(endOfWeek),
    label: `${toLabel(startOfWeek)}–${toLabel(endOfWeek)}`,
  };
};

// Helper function to generate dates for a full month
export const getMonthDates = (date = new Date()) => {
  const daysOfWeek = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const dates = [];

  // Add padding for days before the first day of the month
  for (let i = 0; i < firstDay.getDay(); i++) {
    dates.push({
      dayOfWeek: daysOfWeek[i],
      dayNumber: null, // Null indicates a padding day
      isCurrentMonth: false,
      dateString: null,
    });
  }

  // Add dates for the current month
  for (let day = firstDay; day <= lastDay; day.setDate(day.getDate() + 1)) {
    const dateString = getLocalDateString(day);
    dates.push({
      dayOfWeek: daysOfWeek[day.getDay()],
      dayNumber: day.getDate(),
      isCurrentMonth: true,
      dateString,
    });
  }

  // Add padding for days after the last day of the month
  const remainingDays = 42 - dates.length; // Ensure 6 rows of 7 days
  for (let i = 0; i < remainingDays; i++) {
    dates.push({
      dayOfWeek: daysOfWeek[(firstDay.getDay() + dates.length) % 7],
      dayNumber: null,
      isCurrentMonth: false,
      dateString: null,
    });
  }

  // Split into weeks (6 weeks of 7 days)
  const weeks = [];
  for (let i = 0; i < dates.length; i += 7) {
    weeks.push(dates.slice(i, i + 7));
  }

  return weeks;
};

// Helper to get a start and end date for a given mode
export const getRangeForMode = (dateString = getLocalDateString(), mode = "day") => {
  const base = createLocalDate(dateString);

  if (mode === "week") {
    // Align with week boundaries
    return getWeekRange(dateString);
  }

  let daysBefore = 0;
  let daysAfter = 0;

  switch (mode) {
    case "day":
      daysBefore = 1;
      daysAfter = 1;
      break;
    case "month":
      daysBefore = 15;
      daysAfter = 15;
      break;
    default:
      daysBefore = 1;
      daysAfter = 1;
  }

  const startDate = new Date(base);
  startDate.setDate(base.getDate() - daysBefore);

  const endDate = new Date(base);
  endDate.setDate(base.getDate() + daysAfter);

  return {
    startDate: getLocalDateString(startDate),
    endDate: getLocalDateString(endDate),
  };
};

export const isSameLocalDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

export const formatReminderWhen = (whenIso) => {
    const d = new Date(whenIso);
    if (Number.isNaN(d.getTime())) return "";

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const timeStr = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

    if (isSameLocalDay(d, today)) return `Today ${timeStr}`;
    if (isSameLocalDay(d, tomorrow)) return `Tomorrow ${timeStr}`;

    // fallback: keep your existing style
    return d.toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };